/**
 * DocsLayout — Renderer des Benutzerhandbuchs (/dokumentation).
 *
 * - Linke Kapitel-Navigation (Kapitel + Abschnitte, ohne Scrollspy)
 * - Suchfeld: filtert Kapitel/Abschnitte über Titel + Text (suchtext())
 * - Anker-Links: #<kapitelId>-<abschnittId> — per URL teilbar; beim Laden
 *   und bei Hash-Wechsel wird zum Ziel gescrollt
 * - „Zur Seite“-Buttons: Deep-Link in die jeweilige App-Seite
 * - Abschnitte mit nurDev erscheinen nur in Dev-/Local-Umgebungen
 */

import { useEffect, useMemo, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { ExternalLink, Info, Link as LinkIcon, Search, ShieldCheck } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { ankerId, suchtext } from "@/docs/types"
import type { DocAbschnitt, DocKapitel } from "@/docs/types"

const IS_DEV_ENV = ["dev", "local"].includes(
  (import.meta.env.VITE_ENV || "local").toLowerCase()
)

/** nurDev-Abschnitte außerhalb von Dev/Local ausblenden */
function sichtbareAbschnitte(kapitel: DocKapitel): DocAbschnitt[] {
  return kapitel.abschnitte.filter((a) => !a.nurDev || IS_DEV_ENV)
}

interface GefiltertesKapitel {
  kapitel: DocKapitel
  abschnitte: DocAbschnitt[]
}

function filterKapitel(kapitel: DocKapitel[], query: string): GefiltertesKapitel[] {
  const q = query.trim().toLowerCase()
  return kapitel
    .map((k) => {
      const alle = sichtbareAbschnitte(k)
      if (!q) return { kapitel: k, abschnitte: alle }
      // Kapiteltitel-Treffer zeigt das ganze Kapitel, sonst nur passende Abschnitte
      if (k.titel.toLowerCase().includes(q)) return { kapitel: k, abschnitte: alle }
      return { kapitel: k, abschnitte: alle.filter((a) => suchtext(a).includes(q)) }
    })
    .filter((e) => e.abschnitte.length > 0)
}

// ---------------------------------------------------------------------------
// Abschnitt
// ---------------------------------------------------------------------------

function AbschnittBlock({ kapitel, abschnitt }: { kapitel: DocKapitel; abschnitt: DocAbschnitt }) {
  const anker = ankerId(kapitel.id, abschnitt.id)
  return (
    <section id={anker} className="scroll-mt-4 space-y-3">
      <h3 className="group flex items-center gap-2 text-base font-semibold">
        {abschnitt.titel}
        <a
          href={`#${anker}`}
          aria-label={`Link zu „${abschnitt.titel}“`}
          title="Direktlink kopierbar aus der Adresszeile"
          className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </a>
      </h3>

      {abschnitt.inhalt.map((absatz, i) => (
        <p key={i} className="text-sm leading-relaxed text-muted-foreground">
          {absatz}
        </p>
      ))}

      {abschnitt.schritte && (
        <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-relaxed">
          {abschnitt.schritte.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}

      {abschnitt.tabelle && (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {abschnitt.tabelle.kopf.map((h, i) => (
                  <TableHead key={i}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {abschnitt.tabelle.zeilen.map((zeile, i) => (
                <TableRow key={i}>
                  {zeile.map((zelle, j) => (
                    <TableCell key={j} className={cn(j === 0 && "font-medium")}>
                      {zelle}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {abschnitt.hinweise && (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          {abschnitt.hinweise.map((h, i) => (
            <p key={i} className="flex items-start gap-2 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{h}</span>
            </p>
          ))}
        </div>
      )}

      {abschnitt.rollen && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Berechtigung: {abschnitt.rollen}</span>
        </p>
      )}

      {(abschnitt.seite || abschnitt.verweise) && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {abschnitt.seite && (
            <Link
              to={abschnitt.seite.href}
              className={cn(buttonVariants({ variant: "outline", size: "sm" }), "gap-1.5")}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {abschnitt.seite.label}
            </Link>
          )}
          {abschnitt.verweise?.map((v) => (
            <a
              key={v.anker}
              href={`#${v.anker}`}
              className="text-xs text-primary underline-offset-4 hover:underline"
            >
              → {v.label}
            </a>
          ))}
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function DocsLayout({ kapitel }: { kapitel: DocKapitel[] }) {
  const [query, setQuery] = useState("")
  const location = useLocation()

  const gefiltert = useMemo(() => filterKapitel(kapitel, query), [kapitel, query])

  // Anker-Deep-Link: beim Laden und bei Hash-Wechsel zum Ziel scrollen
  useEffect(() => {
    if (!location.hash) return
    const el = document.getElementById(location.hash.slice(1))
    el?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [location.hash, gefiltert])

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_1fr]">
      {/* Kapitel-Navigation */}
      <nav aria-label="Kapitel" className="lg:sticky lg:top-0 lg:self-start">
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Dokumentation durchsuchen…"
            aria-label="Dokumentation durchsuchen"
            className="pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {gefiltert.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">Keine Treffer.</p>
        ) : (
          <ul className="space-y-3">
            {gefiltert.map(({ kapitel: k, abschnitte }) => (
              <li key={k.id}>
                <a
                  href={`#${k.id}`}
                  className="text-sm font-semibold text-foreground hover:text-primary"
                >
                  {k.titel}
                </a>
                <ul className="mt-1 space-y-0.5 border-l pl-3">
                  {abschnitte.map((a) => (
                    <li key={a.id}>
                      <a
                        href={`#${ankerId(k.id, a.id)}`}
                        className="block truncate py-0.5 text-xs text-muted-foreground hover:text-primary"
                        title={a.titel}
                      >
                        {a.titel}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </nav>

      {/* Inhalt */}
      <div className="min-w-0 space-y-6">
        {gefiltert.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Keine Abschnitte gefunden für „{query}“.
            </CardContent>
          </Card>
        )}
        {gefiltert.map(({ kapitel: k, abschnitte }) => (
          <Card key={k.id} id={k.id} className="scroll-mt-4">
            <CardContent className="space-y-6 pt-6">
              <header>
                <h2 className="text-xl font-semibold tracking-tight">{k.titel}</h2>
                {k.kurz && <p className="mt-1 text-sm text-muted-foreground">{k.kurz}</p>}
              </header>
              {abschnitte.map((a) => (
                <AbschnittBlock key={a.id} kapitel={k} abschnitt={a} />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
